import { getIsLinkedSsoUser, getIsPreviouslySamlUser } from '@rhg/utils/sso';
import { useMemo } from 'react';
import { useNeedsEmailOptInResponse } from '../registration/use-needs-email-opt-in-response';
import { useNeedsRegistrationConsent } from '../registration/use-needs-registration-consent';
import useAccount from '../use-account';
import { useActiveCircle } from '../use-active-circle';
import { useIsAgeGenderEnabled } from '../use-age-gender-enabled';
import { useAgeGenderStepStatus } from '../use-age-gender-step-status';
import useAppConfig from '../use-app-config';
import { useAutoSelectCircleState } from '../use-auto-select-circle-state';
import useCircles from '../use-circles';
import { useConsentContext } from '../use-consent-context';
import { useConsents } from '../use-consents';
import useHasAcceptedCohortTos from '../use-has-accepted-tos';
import { useInviteCode } from '../use-invite-code';
import { useIsSamlUser } from '../use-is-saml-user';
import { useMostRecentSponsorship } from '../use-most-recent-sponsorship';
import { useStandaloneScreeningResults } from '../use-standalone-screening-results';
import { UserBlocker } from './types';

/**
 * This hook determines if the user requires any blocking logic such as a redirect to a registration step, consent acceptance, reconsent.
 * The resulting logic is managed within the `userBlockerGate`.
 */
export function useUserBlocker() {
    const { NEXT_PUBLIC_AGE_GENDER_ENABLED_DATE: ageGenderEnabledDate } =
        useAppConfig();
    const ageGenderEnabled = useIsAgeGenderEnabled();
    const { account, isLoading: isAccountLoading } = useAccount();
    const { activeCircle, isLoading: isActiveCircleLoading } =
        useActiveCircle();
    const { hasInviteCode, isReady: isInviteCodeReady } = useInviteCode();
    const { hasCircles, isLoading: isCirclesLoading } = useCircles();
    const { isCircleSelectionLoaded } = useAutoSelectCircleState();

    const { latestResult } = useStandaloneScreeningResults();
    const { consent, isLoading: isConsentLoading } =
        useHasAcceptedCohortTos(undefined);
    const {
        needsEmailOptInResponse,
        isLoading: isNeedsEmailOptInResponseLoading,
    } = useNeedsEmailOptInResponse();
    const { isLoading: isSponsorLoading, hasLostSponsorship } =
        useMostRecentSponsorship();
    const { consents, isLoading: isGetConsentsLoading } = useConsents();
    const isSamlUser = useIsSamlUser();
    const isPreviouslySamlUser = getIsPreviouslySamlUser();
    const isLinkedSsoUser = getIsLinkedSsoUser();
    const {
        data: needsRegistrationConsentData,
        isLoading: isNeedsRegistrationLoading,
    } = useNeedsRegistrationConsent();
    const accountConsents = consent?.consentData ? [consent.consentData] : [];
    const accountUserConsents = consent?.userResponse
        ? [consent.userResponse]
        : [];
    const { setHasCompletedAgeGenderStep } = useAgeGenderStepStatus();

    // "Legacy" user in this context refers to users whose accounts were created before the ageGender feature was enabled.
    const isAgeGenderLegacyUser = useMemo(() => {
        if (account?.createdAt) {
            const newEnabledDate = new Date(ageGenderEnabledDate);
            return new Date(account.createdAt) < newEnabledDate;
        }
        return false;
    }, [account?.createdAt, ageGenderEnabledDate]);

    const { consentRequirements } = useConsentContext({
        accountConsents,
        accountUserConsents,
        sponsorConsents: consents,
    });

    const parentalConsent = useMemo(
        () => consents?.find((c) => c.type === 'parental'),
        [consents]
    );

    const isLoading =
        isAccountLoading ||
        !isCircleSelectionLoaded ||
        !isInviteCodeReady ||
        isCirclesLoading ||
        isActiveCircleLoading ||
        isNeedsEmailOptInResponseLoading ||
        isSponsorLoading ||
        isConsentLoading ||
        isGetConsentsLoading ||
        isNeedsRegistrationLoading;

    if (isLoading) {
        return { isLoading, userBlocker: undefined };
    }

    if (!isSamlUser && (!hasCircles || !activeCircle)) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.Tos,
        };
    }

    if (hasLostSponsorship) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.Expired,
        };
    }

    if ((isSamlUser || isPreviouslySamlUser) && !isLinkedSsoUser) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.LinkingSSO,
        };
    }

    // if the user is using the promo code and has gone through the standalone screening then they need to be registered and post their results
    if (latestResult) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.PostMentalHealthScreeningPromo,
        };
    }

    // Should be before unsponsored CCHD since unsponsored CCHD will be covered in this flow if needed
    if (!activeCircle?.sponsor && !account?.countryCode) {
        return {
            isLoading: false,
            userBlocker:
                // If invitation flow we handle the country in the invitation flow
                hasInviteCode
                    ? UserBlocker.ReceivingInvitation
                    : UserBlocker.CountryUnsponsored,
        };
    }

    // Should be before sponsor in case unsponsored CCHD version is updated
    if (!activeCircle?.sponsor && consentRequirements.chdpp.isRequired) {
        if (
            !accountUserConsents[0].chdppVersion ||
            accountUserConsents[0].chdppVersion < 1
        ) {
            // If SSO flow, return to attempt joining again
            if (isLinkedSsoUser) {
                return {
                    isLoading: false,
                    userBlocker: UserBlocker.LinkingSSO,
                };
            }

            // If invitation flow we handle the CCHD in the invitation flow
            if (hasInviteCode) {
                return {
                    isLoading: false,
                    userBlocker: UserBlocker.ReceivingInvitation,
                };
            }

            // If non SSO flow, just prompt to accept consent
            return {
                isLoading: false,
                userBlocker: UserBlocker.CCHDUnsponsored,
            };
        } else {
            return {
                isLoading: false,
                userBlocker: UserBlocker.Reconsent,
            };
        }
    }

    // Should be after sponsored CCHD since we need to submit first to determine if the user still needs a country code
    if (activeCircle?.sponsor && !account?.countryCode) {
        return {
            isLoading: false,
            userBlocker:
                // If invitation flow we handle the country in the invitation flow
                hasInviteCode
                    ? UserBlocker.ReceivingInvitation
                    : UserBlocker.CountrySponsored,
        };
    }

    if (ageGenderEnabled && !isAgeGenderLegacyUser && !account?.dob) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.RegistrationAgeGender,
        };
    }

    // This has to be before the unsponsored check otherwise the user can use the app without accepting by bailing out of the flow
    if (
        ageGenderEnabled &&
        parentalConsent &&
        consentRequirements.parental.isRequired
    ) {
        if (
            !accountUserConsents[0].parentalVersion ||
            accountUserConsents[0].parentalVersion < 1
        ) {
            if (
                needsRegistrationConsentData?.needsRegistrationConsent ===
                'true'
            ) {
                // Just in case the user no longer has the age/gender step cookie, we'll set it here to prevent an unnecessary step display
                setHasCompletedAgeGenderStep();
                return {
                    isLoading: false,
                    userBlocker: UserBlocker.RegistrationParentalConsent,
                };
            }

            return {
                isLoading: false,
                userBlocker: UserBlocker.StandaloneParentalConsent,
            };
        } else {
            return {
                isLoading: false,
                userBlocker: UserBlocker.Reconsent,
            };
        }
    }

    // if the user still needs to consent to TOS/Privacy, CHDPP, or Partner Authorization, send them to re-consent flow
    if (
        consentRequirements.tos.isReconsent ||
        consentRequirements.privacy.isReconsent ||
        consentRequirements.chdpp.isReconsent ||
        consentRequirements.uk_international.isReconsent ||
        consentRequirements.parental.isReconsent ||
        consentRequirements.gina.isReconsent
    ) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.Reconsent,
        };
    }
    if (!activeCircle?.sponsor) {
        // If SSO flow, return to attempt joining again
        if (isLinkedSsoUser) {
            return {
                isLoading: false,
                userBlocker: UserBlocker.LinkingSSO,
            };
        }

        /**
         * No invitation handling here. If the user has an invitation, they will get a dialog from the registration dialog manager
         */
        return {
            isLoading: false,
            userBlocker: UserBlocker.Unsponsored,
        };
    }

    if (activeCircle?.sponsor && consentRequirements.chdpp.isRequired) {
        return { isLoading: false, userBlocker: UserBlocker.CCHDSponsored };
    }

    if (
        activeCircle?.sponsor &&
        consentRequirements.uk_international.isRequired
    ) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.UK,
        };
    }

    // Even if they are sponsored, we want to show this
    if (needsEmailOptInResponse) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.CommunicationsPreference,
        };
    }

    return { isLoading: false, userBlocker: undefined };
}
